/**
 * Staff S2 (AB#2205) — the signed-in staff member's own identity, day and
 * schedule, as the backend returns them (GET /api/salon-staff/me, /my-day,
 * /my-schedule). The backend owns every rule (salon-local "today", shift,
 * absence effect, next booking, counts); the web only renders these shapes.
 * Optional fields may be absent (the API omits nulls).
 */
import type { SalonStaffAppointmentRowDto } from '../services/appointmentsList';

export interface StaffBranchRef {
    id: number;
    name: string;
    nameAr?: string | null;
}

/** GET /api/salon-staff/me */
export interface StaffMeDto {
    staffId: number;
    displayName?: string | null;
    firstName?: string | null;
    /** Owner-entered job title (e.g. "Stylist"). */
    position?: string | null;
    /** User-facing role label — always "Staff" in the pilot (SG-09). */
    roleLabel?: string | null;
    businessId?: number;
    businessName?: string | null;
    businessNameAr?: string | null;
    branches?: StaffBranchRef[] | null;
    /** True when the staff member is not restricted to specific branches. */
    allBranches?: boolean;
    photoUrl?: string | null;
    timeZone?: string | null;
}

/** One working window at one branch, "HH:mm" salon-local. */
export interface StaffShiftSegment {
    branchId?: number | null;
    branchName?: string | null;
    branchNameAr?: string | null;
    start: string;
    end: string;
    /** Where the window comes from (e.g. per-branch schedule vs default hours) — informational. */
    source?: string | null;
}

/** Absence type: the enum name (wire convention) or its number (0..4). */
export type StaffAbsenceType = string | number;

export interface StaffDayAbsence {
    id: number;
    type: StaffAbsenceType;
    typeLabelKey?: string | null;
    isFullDay: boolean;
    start?: string | null;
    end?: string | null;
    note?: string | null;
    isRecurring?: boolean;
    branchId?: number | null;
}

export type StaffShiftStatus = 'working' | 'off' | 'absent';

export interface StaffDayShift {
    status: StaffShiftStatus;
    segments?: StaffShiftSegment[] | null;
    /** Set with status "working" too: an intra-day break / emergency window. */
    absence?: StaffDayAbsence | null;
    /** The salon (or the staff member's branch) is closed that day. */
    salonClosed?: boolean;
}

export interface StaffDayCounts {
    pending?: number;
    confirmed?: number;
    inVisit?: number;
    completed?: number;
    cancelledOrNoShow?: number;
}

export interface StaffPendingActions {
    awaitingConfirmation?: number;
    unpaidCompleted?: number;
}

/** A staff booking notification as My Day lists it ("What changed"). */
export interface StaffRecentChange {
    notificationId: number;
    type: string;
    appointmentId?: number | null;
    createdAt: string;
    isRead: boolean;
    /** Tolerated when the backend includes the notification payload. */
    data?: StaffBookingNotificationData | string | null;
}

/** GET /api/salon-staff/my-day */
export interface StaffMyDayDto {
    date: string;
    /** True when `date` is the salon-local today. */
    isToday?: boolean;
    now?: string | null;
    shift?: StaffDayShift | null;
    appointments?: SalonStaffAppointmentRowDto[] | null;
    nextAppointmentId?: number | null;
    counts?: StaffDayCounts | null;
    pendingActions?: StaffPendingActions | null;
    recentChanges?: StaffRecentChange[] | null;
}

export interface StaffWeeklyDay {
    /** 0 = Sunday … 6 = Saturday. */
    dayOfWeek: number;
    isWorking: boolean;
    segments?: StaffShiftSegment[] | null;
}

export interface StaffScheduleAbsence {
    id: number;
    type: StaffAbsenceType;
    isFullDay: boolean;
    startDate: string;
    endDate: string;
    startTime?: string | null;
    endTime?: string | null;
    note?: string | null;
    /** A recurring window (e.g. daily break) repeating every day from startDate. */
    isRecurring?: boolean;
    /** The branch the absence applies to, when it is branch-specific. */
    branchId?: number | null;
}

export interface StaffScheduleHoliday {
    date: string;
    name?: string | null;
    nameAr?: string | null;
    branchIds?: number[] | null;
}

/** GET /api/salon-staff/my-schedule */
export interface StaffMyScheduleDto {
    weekly?: StaffWeeklyDay[] | null;
    absences?: StaffScheduleAbsence[] | null;
    holidays?: StaffScheduleHoliday[] | null;
}

/** Staff booking notification types (SG-16). */
export const STAFF_BOOKING_NOTIFICATION_TYPES = [
    'StaffBookingAssigned',
    'StaffBookingChanged',
    'StaffBookingCancelled',
] as const;
export type StaffBookingNotificationType = typeof STAFF_BOOKING_NOTIFICATION_TYPES[number];

/** `data` of a staff booking notification (never customer phone / email). */
export interface StaffBookingNotificationData {
    appointmentId?: number | null;
    date?: string | null;
    startTime?: string | null;
    serviceName?: string | null;
    serviceNameAr?: string | null;
    branchName?: string | null;
    branchNameAr?: string | null;
    customerFirstName?: string | null;
    /** e.g. "reassignedAway" on StaffBookingChanged. */
    change?: string | null;
    /** Customer | Salon | SalonDeclined | Expired on StaffBookingCancelled. */
    source?: string | null;
    previousDate?: string | null;
    previousStartTime?: string | null;
}
